import { useEffect, useMemo, useState } from "react";
import { Download, Film, Loader2 } from "lucide-react";
import { parseTimecodes, toPremiereXML, toResolveEDL, toSRT, toFCPXML, download } from "@/lib/timecodeExport";
import { fmtTC } from "@/lib/montage";

const FORMATS = [
  { id: "premiere", label: "Premiere Pro", ext: "xml", type: "application/xml", fn: toPremiereXML, hint: "File › Import, apoi deschide secvența cu markere." },
  { id: "davinci", label: "DaVinci Resolve", ext: "edl", type: "text/plain", fn: toResolveEDL, hint: "Timeline › Import › Timeline Markers from EDL." },
  { id: "capcut", label: "CapCut", ext: "srt", type: "application/x-subrip", fn: (it) => toSRT(it), hint: "Text › Subtitrări locale › încarcă fișierul." },
  { id: "finalcut", label: "Final Cut Pro", ext: "fcpxml", type: "application/xml", fn: toFCPXML, hint: "File › Import › XML; markerele apar pe proiect." },
];
const safe = (s) => String(s || "clip").replace(/[\\/:*?"<>|]+/g, " ").trim().slice(0, 80);

export default function PublicTimecode({ token }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const want = new URLSearchParams(window.location.search).get("f");
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/tc/${token}`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setD).catch(() => setErr("Linkul nu mai e valid."));
  }, [token]);
  const items = useMemo(() => parseTimecodes(d?.tc_text || ""), [d]);
  const get = (f) => download(f.fn(items, d.title || "clip", 25), `${safe(d.title)} - ${f.label}.${f.ext}`, f.type);
  if (err) return <p className="p-8 text-center text-zinc-400" data-testid="ptc-error">{err}</p>;
  if (!d) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>;
  const list = want && FORMATS.some((f) => f.id === want) ? [FORMATS.find((f) => f.id === want), ...FORMATS.filter((f) => f.id !== want)] : FORMATS;
  return (
    <div className="mx-auto max-w-xl space-y-5 p-5 sm:p-8" data-testid="public-timecode">
      <div><p className="text-sm text-zinc-500">{d.client}</p><h1 className="font-display text-3xl font-bold">{d.title}</h1>
        {d.post_date && <p className="mt-1 text-sm text-amber-500">Deadline: {d.post_date.slice(8, 10)}.{d.post_date.slice(5, 7)} {d.post_time}</p>}</div>
      <section className="surface-card space-y-2 rounded-3xl border border-zinc-800 bg-[#12141C] p-4">
        <h2 className="flex items-center gap-2 font-semibold"><Film className="h-4 w-4 text-amber-500" />Fișier de import</h2>
        {list.map((f, i) => (
          <button key={f.id} type="button" onClick={() => get(f)} disabled={!items.length} data-testid={`ptc-dl-${f.id}`}
            className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left disabled:opacity-40 ${i === 0 && want ? "bg-amber-500 text-[#0B0D13]" : "border border-zinc-700"}`}>
            <Download className="h-5 w-5 shrink-0" /><span><b className="block">{f.label} (.{f.ext})</b><span className="text-xs opacity-75">{f.hint}</span></span>
          </button>
        ))}
      </section>
      <section className="surface-card rounded-3xl border border-zinc-800 bg-[#12141C] p-4">
        <h2 className="mb-2 font-semibold">Timecode-uri ({items.length})</h2>
        <ol className="divide-y divide-zinc-800">{items.map((t, i) => (
          <li key={`${i}-${t.start}`} className="flex gap-3 py-2 text-sm">{t.cam && <span className="rounded bg-sky-500/20 px-1.5 text-xs font-bold text-sky-300">{t.cam}</span>}
            <span className="font-mono font-bold text-amber-500">{fmtTC(t.start)}{t.end != null ? ` – ${fmtTC(t.end)}` : ""}</span><span className="text-zinc-300">{t.note}</span></li>))}</ol>
      </section>
    </div>
  );
}
